import React, { useState, useEffect } from 'react';
import { useT } from '../BuilderApp.jsx';
import WizardShell from '../WizardShell.jsx';
import { tmplById } from '../../data/defaults.js';
import { walletLabel } from '../../data/packages.js';
import { getConcept } from '../../concepts.js';

function StepNextHint({ step }) {
  const t = useT();
  const msg = t[`next${step}`];
  if (!msg) return null;
  return (
    <div style={{display:'flex',alignItems:'center',gap:'.4rem',justifyContent:'center',marginTop:'.55rem',fontSize:'.72rem',color:'rgba(148,163,184,.55)',letterSpacing:'.01em',lineHeight:1.4}}>
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none" style={{flexShrink:0,opacity:.55}}>
        <path d="M2 5.5h7M6 2.5l3 3-3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <span>{msg}</span>
    </div>
  );
}

// Map template ID → restaurant concept (null for non-restaurant templates)
function getConceptForTemplate(templateId) {
  const MAP = {
    'restaurant-v1': 'thai',
    'asian-v1': 'thai',
    'cafe-v1': 'pizza',
    'pizza-v1': 'pizza',
  };
  const key = MAP[templateId];
  return key ? getConcept(key) : null;
}

// Scaled-down restaurant preview component
function RestaurantPreview({ config, concept, compact }) {
  const [imgIdx, setImgIdx] = useState(0);
  const [activeTab, setActiveTab] = useState(0);

  const chili = config.branding.primary || concept.theme.chili;
  const name = config.branding.company_name || concept.restaurant.name;
  const logo = config.branding.logo_dataUrl || config.branding.logo_url;
  const h1 = (config.content && config.content.hero_headline) || concept.heroH1;
  const heroSub = (config.content && config.content.hero_sub) || '';
  const about = (config.content && config.content.about) || '';
  const email = (config.content && config.content.contact_email) || '';
  const phone = (config.content && config.content.contact_phone) || concept.restaurant.phone;
  const addr = (config.content && config.content.address) || concept.restaurant.address;
  const heroImgs = concept.restaurant.heroImages;
  const bg = concept.theme.bg;
  const ink = concept.theme.ink;
  const ln = concept.theme.line;
  const p = compact;

  useEffect(() => {
    const id = setInterval(() => setImgIdx(i => (i + 1) % heroImgs.length), 4000);
    return () => clearInterval(id);
  }, [heroImgs.length]);

  const uns = (photoId, w = 600) =>
    /^https?:/.test(photoId) ? photoId
      : `https://images.unsplash.com/${photoId}?auto=format&fit=crop&w=${w}&q=70`;

  const cats = concept.cats;
  const activeCat = cats[activeTab] || cats[0];
  const shown = concept.menu.filter(m => m.cat === activeCat.id).slice(0, p ? 2 : 3);
  const heroH = p ? 210 : 360;

  return (
    <div style={{fontFamily:"'Inter',system-ui,sans-serif",background:bg,color:ink,overflowX:'hidden',userSelect:'none'}}>
      {/* HERO */}
      <div style={{position:'relative',height:heroH,overflow:'hidden',flexShrink:0}}>
        {heroImgs.map((imgId, i) => (
          <img key={i} src={uns(imgId, p ? 600 : 1100)} alt=""
            style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover',opacity:i === imgIdx ? 1 : 0,transition:'opacity 1.4s ease'}}
            onError={e => { e.target.style.opacity = 0; }}/>
        ))}
        <div style={{position:'absolute',inset:0,background:'linear-gradient(180deg,rgba(20,15,10,.32) 0%,rgba(20,15,10,.04) 36%,rgba(20,15,10,.86) 100%)'}}/>
        {/* Nav */}
        <div style={{position:'absolute',top:0,left:0,right:0,padding:p ? '10px 14px' : '14px 22px',display:'flex',alignItems:'center',justifyContent:'space-between',zIndex:3}}>
          <div style={{display:'flex',alignItems:'center',gap:p ? 7 : 10}}>
            <div style={{width:p ? 30 : 38,height:p ? 30 : 38,borderRadius:10,background:chili,display:'flex',alignItems:'center',justifyContent:'center',fontSize:p ? 14 : 18,overflow:'hidden',flexShrink:0}}>
              {logo ? <img src={logo} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/> : <span>🍽️</span>}
            </div>
            <span style={{fontWeight:800,fontSize:p ? 13 : 17,color:'#fff',textShadow:'0 1px 8px rgba(0,0,0,.55)'}}>{name}</span>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:p ? 6 : 10}}>
            <span style={{color:'#fff',fontSize:p ? 10 : 12,fontWeight:600,background:'rgba(255,255,255,.18)',backdropFilter:'blur(6px)',padding:p ? '4px 9px' : '6px 13px',borderRadius:20,whiteSpace:'nowrap'}}>Åpen nå</span>
            <div style={{width:p ? 28 : 36,height:p ? 28 : 36,borderRadius:10,background:chili,display:'flex',alignItems:'center',justifyContent:'center',fontSize:p ? 13 : 17,flexShrink:0}}>🛒</div>
          </div>
        </div>
        {/* Hero copy */}
        <div style={{position:'absolute',bottom:0,left:0,right:0,padding:`0 ${p ? 14 : 24}px ${p ? 16 : 28}px`,color:'#fff',zIndex:2}}>
          <div style={{fontFamily:'Georgia,serif',fontSize:p ? 17 : 32,fontWeight:700,lineHeight:1.08,marginBottom:p ? 7 : 12,textShadow:'0 2px 18px rgba(0,0,0,.5)',maxWidth:'88%'}}>{h1}</div>
          <div style={{display:'flex',gap:p ? 8 : 10,flexWrap:'wrap'}}>
            <button style={{background:chili,color:'#fff',fontWeight:700,fontSize:p ? 11 : 15,padding:p ? '8px 14px' : '12px 22px',borderRadius:12,border:'none',cursor:'pointer'}}>
              🍴 Se menyen
            </button>
          </div>
        </div>
      </div>

      {/* INFO CHIPS */}
      <div style={{background:'#fff',padding:p ? '8px 14px' : '10px 22px',display:'flex',gap:p ? 7 : 10,overflowX:'auto',borderBottom:`1px solid ${ln}`}}>
        {[
          {icon:'📍', text: addr.split(',')[0] || 'Din adresse'},
          {icon:'⏰', text: concept.restaurant.hours},
          {icon:'💳', text: 'Vipps · Kort'},
        ].map(c => (
          <div key={c.text} style={{display:'flex',alignItems:'center',gap:5,whiteSpace:'nowrap',fontSize:p ? 11 : 12.5,fontWeight:600,color:'#6f675c',background:'#fff',padding:p ? '6px 10px' : '7px 14px',borderRadius:12,boxShadow:'0 2px 8px rgba(28,26,23,.08)',border:`1px solid ${ln}`,flexShrink:0}}>
            <span style={{fontSize:p ? 13 : 15,color:chili}}>{c.icon}</span>{c.text}
          </div>
        ))}
      </div>

      {/* CATEGORY NAV */}
      <div style={{position:'sticky',top:0,zIndex:10,background:bg,borderBottom:`1px solid ${ln}`,padding:p ? '0 12px' : '0 22px'}}>
        <div style={{display:'flex',gap:p ? 6 : 8,padding:p ? '8px 0' : '11px 0',overflowX:'auto'}}>
          {cats.map((c, i) => (
            <button key={c.id} onClick={() => setActiveTab(i)} style={{
              whiteSpace:'nowrap',fontSize:p ? 11.5 : 14,fontWeight:600,
              padding:p ? '6px 12px' : '8px 18px',borderRadius:22,border:'none',cursor:'pointer',
              background:i === activeTab ? ink : '#fff',color:i === activeTab ? '#fff' : '#6f675c',
              boxShadow:'0 2px 8px rgba(28,26,23,.08)',flexShrink:0,
            }}>{c.name}</button>
          ))}
        </div>
      </div>

      {/* MENU */}
      <div style={{padding:p ? '14px 12px 20px' : '20px 22px 28px'}}>
        <div style={{fontFamily:'Georgia,serif',fontSize:p ? 18 : 24,fontWeight:700,marginBottom:p ? 10 : 16,color:ink}}>{activeCat.name}</div>
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {shown.map(d => (
            <div key={d.id} style={{background:'#fff',borderRadius:14,overflow:'hidden',display:'flex',boxShadow:'0 3px 12px rgba(28,26,23,.08)',border:`1px solid ${ln}`}}>
              <div style={{width:80,height:80,flexShrink:0,overflow:'hidden',background:'linear-gradient(135deg,#f3d9b0,#e7a86a)'}}>
                <img src={uns(d.img, 200)} alt={d.name} onError={e => {e.target.style.opacity = 0;}} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
              </div>
              <div style={{padding:'10px 13px',flex:1,display:'flex',flexDirection:'column',justifyContent:'center',minWidth:0}}>
                <div style={{fontWeight:600,fontSize:13.5,lineHeight:1.3,marginBottom:3,color:ink,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{d.name}</div>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                  <span style={{fontWeight:700,fontSize:13,color:ink}}>{d.price} kr</span>
                  <button style={{background:chili,color:'#fff',fontWeight:700,fontSize:11,padding:'5px 11px',borderRadius:18,border:'none',cursor:'pointer',flexShrink:0}}>+ Legg til</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FOOTER */}
      <div style={{background:ink,padding:p ? '18px 14px' : '28px 22px'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div style={{fontWeight:800,fontSize:p ? 14 : 18,color:'#fff'}}>{name}</div>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <div style={{width:p ? 28 : 32,height:p ? 28 : 32,borderRadius:8,background:chili,display:'flex',alignItems:'center',justifyContent:'center',fontSize:p ? 13 : 16}}>🍽️</div>
            <span style={{fontSize:p ? 11 : 12,color:'rgba(255,255,255,.35)'}}>Drevet av Chamcore</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const PALETTES = [
  { label:'Ocean',  a:'#2563eb', b:'#7c3aed' },
  { label:'Forest', a:'#059669', b:'#0d9488' },
  { label:'Sunset', a:'#dc2626', b:'#ea580c' },
  { label:'Rose',   a:'#be185d', b:'#7c3aed' },
  { label:'Slate',  a:'#334155', b:'#1e293b' },
  { label:'Gold',   a:'#d97706', b:'#b45309' },
];
const RESTAURANT_PALETTES = [
  { label:'Thai Red',  a:'#e23b2e', b:'#1f7a52' },
  { label:'Pizza Red', a:'#c8102e', b:'#2e7d32' },
  { label:'Forest',    a:'#1f7a52', b:'#e8a13a' },
  { label:'Charcoal',  a:'#2d2d2d', b:'#c8102e' },
  { label:'Orange',    a:'#ea580c', b:'#d97706' },
  { label:'Midnight',  a:'#1e1b4b', b:'#c8102e' },
];

export default function Step5Preview({ config, setField, next, back, lang, setLang }) {
  const t = useT();
  const upd = (k, v) => setField('branding', { ...config.branding, [k]: v });
  const updC = (k, v) => setField('content', { ...config.content, [k]: v });
  const tmpl = tmplById(config.template);
  const primary = config.branding.primary || '#3b82f6';
  const secondary = config.branding.secondary || '#6366f1';
  const name = config.branding.company_name || (lang === 'no' ? 'Din bedrift' : 'Your Business');
  const headline = config.content.hero_headline || (lang === 'no' ? 'Velkommen!' : 'Welcome!');
  const sub = config.content.hero_sub || (lang === 'no' ? 'Vi ser frem til å betjene deg.' : 'We look forward to serving you.');
  const logoSrc = config.branding.logo_dataUrl || config.branding.logo_url || null;
  const no = ['no', 'sv', 'da'].includes(lang);
  const [fullscreen, setFullscreen] = useState(false);

  const slug = name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'mybusiness';

  const concept = getConceptForTemplate(config.template);
  const isRestaurant = !!concept;
  const activePalettes = isRestaurant ? RESTAURANT_PALETTES : PALETTES;

  // Fullscreen modal
  if (fullscreen) {
    return (
      <div style={{position:'fixed',inset:0,zIndex:300,display:'flex',flexDirection:'column',background:'#fff'}}>
        {/* Chrome */}
        <div style={{background:'#1a2030',padding:'.55rem 1rem',display:'flex',alignItems:'center',gap:'.75rem',flexShrink:0,borderBottom:'2px solid rgba(37,99,235,.4)'}}>
          <div style={{display:'flex',gap:'.35rem'}}>
            {['#f87171','#fbbf24','#34d399'].map(c => <div key={c} style={{width:10,height:10,borderRadius:'50%',background:c}}/>)}
          </div>
          <div style={{flex:1,background:'rgba(255,255,255,.07)',borderRadius:6,padding:'.25rem .75rem',fontSize:'.72rem',color:'var(--dim)',textAlign:'center',maxWidth:320,margin:'0 auto'}}>
            {slug}.com
          </div>
          <div style={{display:'flex',alignItems:'center',gap:'.5rem',flexShrink:0}}>
            <div style={{fontSize:'.65rem',color:'#60a5fa',fontWeight:700,background:'rgba(37,99,235,.15)',border:'1px solid rgba(37,99,235,.3)',borderRadius:6,padding:'.2rem .55rem'}}>
              {t.fs_edit_btn}
            </div>
            <button onClick={() => setFullscreen(false)} style={{background:'rgba(255,255,255,.08)',border:'1px solid rgba(255,255,255,.18)',borderRadius:8,color:'#fff',padding:'.3rem .75rem',cursor:'pointer',fontSize:'.75rem',fontWeight:700}}>
              {t.fs_close}
            </button>
          </div>
        </div>
        {/* Colour bar */}
        <div style={{background:'#0f172a',borderBottom:'1px solid rgba(255,255,255,.08)',padding:'.45rem 1rem',display:'flex',alignItems:'center',gap:'.75rem',flexShrink:0,overflowX:'auto'}}>
          <span style={{fontSize:'.65rem',color:'var(--dim)',fontWeight:700,whiteSpace:'nowrap'}}>🎨</span>
          {activePalettes.map(pl => (
            <button key={pl.label} onClick={() => setField('branding', { ...config.branding, primary: pl.a, secondary: pl.b })}
              title={pl.label}
              style={{width:24,height:24,borderRadius:6,border:`2px solid ${config.branding.primary === pl.a ? '#fff' : 'transparent'}`,background:`linear-gradient(135deg,${pl.a},${pl.b})`,cursor:'pointer',flexShrink:0}}
            />
          ))}
          <input type="color" value={primary} onChange={e => upd('primary', e.target.value)} style={{width:28,height:28,borderRadius:6,border:'1px solid rgba(255,255,255,.15)',background:'none',cursor:'pointer',padding:1,flexShrink:0}}/>
          <button className="btn btn-primary btn-sm" onClick={() => { setFullscreen(false); next(); }} style={{marginLeft:'auto',flexShrink:0,fontSize:'.72rem',padding:'.35rem .9rem'}}>
            {t.s5p_continue}
          </button>
        </div>
        {/* Content */}
        {isRestaurant ? (
          <div style={{flex:1,overflowY:'auto'}}>
            <RestaurantPreview config={config} concept={concept}/>
          </div>
        ) : (
          <div style={{flex:1,overflowY:'auto',background:'#fff',color:'#111'}}>
            <div style={{background:primary,padding:'.75rem 1.5rem',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:10}}>
              {logoSrc
                ? <img src={logoSrc} alt="logo" style={{height:28,maxWidth:120,objectFit:'contain'}}/>
                : <span style={{fontWeight:900,fontSize:'1rem',color:'#fff'}}>{name}</span>
              }
              <div style={{display:'flex',gap:'1.5rem'}}>
                {(no ? ['Meny','Book','Om oss','Kontakt'] : ['Menu','Book','About','Contact']).map(n => (
                  <span key={n} style={{fontSize:'.82rem',color:'rgba(255,255,255,.85)',fontWeight:600}}>{n}</span>
                ))}
              </div>
            </div>
            <div style={{position:'relative',minHeight:340,display:'flex',alignItems:'center',overflow:'hidden'}}>
              <img src={tmpl.photoUrl} alt="" style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover'}}/>
              <div style={{position:'absolute',inset:0,background:`linear-gradient(135deg,${primary}e0,${secondary}bb)`}}/>
              <div style={{position:'relative',zIndex:1,padding:'3rem 2rem',maxWidth:680}}>
                <div style={{fontWeight:900,fontSize:'clamp(1.5rem,4vw,2.5rem)',color:'#fff',marginBottom:'.6rem',lineHeight:1.1}}>{headline}</div>
                <div style={{fontSize:'1.05rem',color:'rgba(255,255,255,.85)',marginBottom:'1.25rem',maxWidth:'70%',lineHeight:1.6}}>{sub}</div>
                <div style={{display:'flex',gap:'.75rem',flexWrap:'wrap'}}>
                  <div style={{display:'inline-flex',alignItems:'center',background:'#fff',borderRadius:9,padding:'.5rem 1.25rem',fontSize:'.9rem',fontWeight:700,color:primary}}>
                    {no ? '📅 Book time →' : '📅 Book now →'}
                  </div>
                </div>
              </div>
            </div>
            <div style={{background:primary,padding:'1.25rem 2rem',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span style={{fontWeight:900,fontSize:'.95rem',color:'#fff'}}>{name}</span>
              <span style={{fontSize:'.75rem',color:'rgba(255,255,255,.55)'}}>{no ? 'Drevet av Chamcore' : 'Powered by Chamcore'}</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <WizardShell step={5} total={8} title={t.s5p_title} subtitle={t.s5p_sub} onBack={back} lang={lang} setLang={setLang}>
      <div style={{display:'flex',flexDirection:'column',gap:'1.5rem'}}>

        {/* Preview box */}
        <div style={{borderRadius:16,overflow:'hidden',border:'1px solid var(--border)',boxShadow:'0 8px 40px rgba(0,0,0,.4)'}}>
          {/* Browser chrome */}
          <div style={{background:'#1a2030',padding:'.6rem .9rem',display:'flex',alignItems:'center',gap:'.5rem',borderBottom:'1px solid var(--border)'}}>
            <div style={{display:'flex',gap:'.35rem'}}>
              {['#f87171','#fbbf24','#34d399'].map(c => <div key={c} style={{width:9,height:9,borderRadius:'50%',background:c}}/>)}
            </div>
            <div style={{flex:1,background:'rgba(255,255,255,.07)',borderRadius:5,padding:'.22rem .6rem',fontSize:'.65rem',color:'var(--dim)',textAlign:'center',maxWidth:220,margin:'0 auto',overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis'}}>
              {slug}.com
            </div>
            <button
              onClick={() => setFullscreen(true)}
              style={{background:'rgba(255,255,255,.1)',border:'1px solid rgba(255,255,255,.15)',borderRadius:6,color:'#94a3b8',padding:'.2rem .5rem',cursor:'pointer',fontSize:'.65rem',fontWeight:700,flexShrink:0}}
            >
              ⛶ {no ? 'Fullside' : 'Full page'}
            </button>
          </div>

          {/* Site preview */}
          {isRestaurant ? (
            <div style={{maxHeight:'72vh',overflowY:'auto'}}>
              <RestaurantPreview config={config} concept={concept} compact/>
            </div>
          ) : (
            <div style={{background:'#fff',color:'#111',maxHeight:'72vh',overflowY:'auto'}}>
              {/* Nav */}
              <div style={{background:primary,padding:'.6rem 1.1rem',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:10}}>
                {logoSrc
                  ? <img src={logoSrc} alt="logo" style={{height:22,maxWidth:90,objectFit:'contain'}}/>
                  : <span style={{fontWeight:900,fontSize:'.8rem',color:'#fff'}}>{name}</span>
                }
                <div style={{display:'flex',gap:'1rem'}}>
                  {(no ? ['Meny','Book','Om oss','Kontakt'] : ['Menu','Book','About','Contact']).map(n => (
                    <span key={n} style={{fontSize:'.6rem',color:'rgba(255,255,255,.85)',fontWeight:600}}>{n}</span>
                  ))}
                </div>
              </div>
              {/* Hero */}
              <div style={{position:'relative',minHeight:200,display:'flex',alignItems:'center',overflow:'hidden'}}>
                <img src={tmpl.photoUrl} alt="" style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover'}}/>
                <div style={{position:'absolute',inset:0,background:`linear-gradient(135deg,${primary}dd,${secondary}99)`}}/>
                <div style={{position:'relative',zIndex:1,padding:'2rem 1.25rem'}}>
                  <div style={{fontWeight:900,fontSize:'clamp(1.1rem,4vw,1.75rem)',color:'#fff',marginBottom:'.4rem',lineHeight:1.15}}>{headline}</div>
                  <div style={{fontSize:'.72rem',color:'rgba(255,255,255,.85)',marginBottom:'.9rem',maxWidth:'70%',lineHeight:1.55}}>{sub}</div>
                  <div style={{display:'flex',gap:'.5rem',flexWrap:'wrap'}}>
                    <div style={{display:'inline-flex',alignItems:'center',background:'#fff',borderRadius:7,padding:'.35rem .85rem',fontSize:'.68rem',fontWeight:700,color:primary}}>
                      {no ? '📅 Book time →' : '📅 Book now →'}
                    </div>
                    <div style={{display:'inline-flex',alignItems:'center',background:'rgba(255,255,255,.18)',border:'1px solid rgba(255,255,255,.35)',borderRadius:7,padding:'.35rem .85rem',fontSize:'.68rem',fontWeight:600,color:'#fff'}}>
                      {no ? '🟠 Betal i samtale' : '🟠 Pay by phone'}
                    </div>
                  </div>
                </div>
              </div>
              {/* Trust strip */}
              <div style={{display:'flex',background:'#f8fafc',borderBottom:'1px solid #e5e7eb'}}>
                {[
                  {i:'📅', l:no ? 'Booking' : 'Booking'},
                  {i:'🟠', l:walletLabel(lang)},
                  {i:'📞', l:no ? 'Telefon-AI' : 'Phone AI'},
                  {i:'🧾', l:no ? 'Auto-faktura' : 'Auto invoice'},
                ].map((f, idx) => (
                  <div key={f.l} style={{flex:1,padding:'.55rem .4rem',textAlign:'center',borderRight:idx < 3 ? '1px solid #e5e7eb' : 'none'}}>
                    <div style={{fontSize:'.8rem',marginBottom:'.15rem'}}>{f.i}</div>
                    <div style={{fontSize:'.55rem',fontWeight:700,color:'#374151',lineHeight:1.2}}>{f.l}</div>
                  </div>
                ))}
              </div>
              {/* About */}
              <div style={{padding:'1.5rem 1.25rem',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1rem',alignItems:'center'}}>
                <div>
                  <div style={{fontSize:'.6rem',fontWeight:800,color:primary,letterSpacing:'.1em',textTransform:'uppercase',marginBottom:'.4rem'}}>{no ? 'OM OSS' : 'ABOUT US'}</div>
                  <div style={{fontWeight:800,fontSize:'.95rem',marginBottom:'.5rem',lineHeight:1.25,color:'#111'}}>{name}</div>
                  <div style={{fontSize:'.7rem',color:'#4b5563',lineHeight:1.65}}>
                    {config.content.about || (no ? 'Vi er en lokal bedrift med lidenskap for kvalitet og god service.' : 'A local business with a passion for quality and great service.')}
                  </div>
                </div>
                <div style={{borderRadius:12,overflow:'hidden',aspectRatio:'4/3'}}>
                  <img src={tmpl.photoUrl} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                </div>
              </div>
              {/* Footer */}
              <div style={{background:primary,padding:'.85rem 1.25rem',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <span style={{fontWeight:800,fontSize:'.75rem',color:'#fff'}}>{name}</span>
                <span style={{fontSize:'.6rem',color:'rgba(255,255,255,.55)'}}>{no ? 'Drevet av Chamcore' : 'Powered by Chamcore'}</span>
              </div>
            </div>
          )}
        </div>

        {/* Colour customiser */}
        <div>
          <div style={{display:'flex',alignItems:'center',gap:'.5rem',marginBottom:'.75rem'}}>
            <span style={{fontSize:'.8rem',fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.06em'}}>🎨 {t.s5p_colors}</span>
          </div>
          {/* Presets */}
          <div style={{display:'flex',flexWrap:'wrap',gap:'.5rem',marginBottom:'.9rem'}}>
            {activePalettes.map(pl => (
              <button
                key={pl.label}
                onClick={() => setField('branding', { ...config.branding, primary: pl.a, secondary: pl.b })}
                title={pl.label}
                style={{
                  width:36,height:36,borderRadius:8,cursor:'pointer',
                  border:`2px solid ${config.branding.primary === pl.a ? '#fff' : 'transparent'}`,
                  background:`linear-gradient(135deg,${pl.a},${pl.b})`,
                  flexShrink:0,transition:'border-color .15s',
                  boxShadow: config.branding.primary === pl.a ? '0 0 0 1px rgba(255,255,255,.3),0 2px 8px rgba(0,0,0,.4)' : 'none',
                }}
              />
            ))}
          </div>
          {/* Custom pickers */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1rem'}}>
            <div>
              <label className="field-label">{t.s3_primary}</label>
              <div style={{display:'flex',gap:'.5rem',alignItems:'center'}}>
                <input type="color" value={primary} onChange={e => upd('primary', e.target.value)} style={{width:42,height:38,border:'1.5px solid var(--border2)',borderRadius:8,padding:2,background:'rgba(255,255,255,.05)',cursor:'pointer',flexShrink:0}}/>
                <input type="text" className="field-input" value={primary} onChange={e => upd('primary', e.target.value)} style={{fontFamily:'monospace',fontSize:'.82rem'}}/>
              </div>
            </div>
            <div>
              <label className="field-label">{t.s3_secondary}</label>
              <div style={{display:'flex',gap:'.5rem',alignItems:'center'}}>
                <input type="color" value={secondary} onChange={e => upd('secondary', e.target.value)} style={{width:42,height:38,border:'1.5px solid var(--border2)',borderRadius:8,padding:2,background:'rgba(255,255,255,.05)',cursor:'pointer',flexShrink:0}}/>
                <input type="text" className="field-input" value={secondary} onChange={e => upd('secondary', e.target.value)} style={{fontFamily:'monospace',fontSize:'.82rem'}}/>
              </div>
            </div>
          </div>
        </div>

        <button className="btn btn-primary btn-lg btn-full" onClick={next}>{t.s5p_continue}</button>
        <StepNextHint step={5}/>
      </div>
    </WizardShell>
  );
}
